/**
 * Cliente HTTP da API OLÉ.
 *
 * Guarda os tokens em memória (o refresh fica no localStorage para sobreviver ao
 * reload) e renova o access token uma única vez por 401 — várias chamadas
 * simultâneas compartilham a mesma renovação em vez de disparar N refreshes.
 */

const BASE = `${import.meta.env.VITE_API_URL ?? ""}/api/v1`;
const CHAVE_REFRESH = "ole.refreshToken";

let accessToken: string | null = null;
let renovacaoEmCurso: Promise<string | null> | null = null;
const ouvintes = new Set<() => void>();

export class ApiError extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = "ApiError";
  }
}

export const sessao = {
  get access() {
    return accessToken;
  },
  get refresh() {
    return localStorage.getItem(CHAVE_REFRESH);
  },
  definir(access: string | null, refresh?: string | null) {
    accessToken = access;
    if (refresh !== undefined) {
      if (refresh) localStorage.setItem(CHAVE_REFRESH, refresh);
      else localStorage.removeItem(CHAVE_REFRESH);
    }
    ouvintes.forEach((fn) => fn());
  },
  limpar() {
    this.definir(null, null);
  },
  aoMudar(fn: () => void) {
    ouvintes.add(fn);
    return () => ouvintes.delete(fn);
  },
};

async function renovar(): Promise<string | null> {
  const refresh = sessao.refresh;
  if (!refresh) return null;
  renovacaoEmCurso ??= (async () => {
    try {
      const resposta = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${refresh}` },
      });
      if (!resposta.ok) {
        sessao.limpar();
        return null;
      }
      const { accessToken: novo } = (await resposta.json()) as { accessToken: string };
      sessao.definir(novo);
      return novo;
    } finally {
      renovacaoEmCurso = null;
    }
  })();
  return renovacaoEmCurso;
}

interface Opcoes extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Para respostas binárias (PDF, CSV). */
  blob?: boolean;
  /** Envio de arquivo — o corpo já vem como FormData. */
  formData?: FormData;
}

async function executar(caminho: string, opcoes: Opcoes, token: string | null): Promise<Response> {
  const headers = new Headers(opcoes.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (opcoes.body !== undefined && !opcoes.formData) headers.set("Content-Type", "application/json");

  return fetch(`${BASE}${caminho}`, {
    ...opcoes,
    headers,
    body: opcoes.formData ?? (opcoes.body === undefined ? undefined : JSON.stringify(opcoes.body)),
  });
}

export async function api<T = unknown>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  let resposta = await executar(caminho, opcoes, accessToken);

  if (resposta.status === 401 && sessao.refresh) {
    const novo = await renovar();
    if (novo) resposta = await executar(caminho, opcoes, novo);
  }

  if (!resposta.ok) {
    let mensagem = `Falha na requisição (${resposta.status})`;
    let detalhes: unknown;
    try {
      const corpo = await resposta.json();
      mensagem = corpo.erro ?? mensagem;
      detalhes = corpo.detalhes;
    } catch {
      /* resposta sem JSON — mantém a mensagem padrão */
    }
    if (resposta.status === 401) sessao.limpar();
    throw new ApiError(resposta.status, mensagem, detalhes);
  }

  if (opcoes.blob) return (await resposta.blob()) as T;
  if (resposta.status === 204) return undefined as T;
  return (await resposta.json()) as T;
}

/** Dispara o download de um blob já baixado da API. */
export function baixar(blob: Blob, nomeArquivo: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoga depois do clique para não cancelar o download em navegadores lentos.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
